import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Heart, Loader2 } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const AdminLogin = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/40 px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md rounded-3xl bg-card border border-border p-8 sm:p-10">
        <div className="flex items-center gap-2 mb-8">
          <span className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-primary-foreground">
            <Heart className="w-5 h-5" />
          </span>
          <span className="font-serif text-xl font-semibold">MyySpace Admin</span>
        </div>
        <h1 className="font-serif text-2xl mb-1">Sign in</h1>
        <p className="text-sm text-muted-foreground mb-6">View and manage session requests.</p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="admin-email-input" className="rounded-xl h-12 bg-background" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" data-testid="admin-password-input" className="rounded-xl h-12 bg-background" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid="admin-login-button" className="rounded-full w-full h-12 mt-6">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
        </Button>
      </form>
    </div>
  );
};

export default AdminLogin;
